import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { copy } from "../copy";
import { ApiError, createExecutor, fetchDraft, fetchRevisions, postApprove, postReturn, postRewrite, postSync, rewakeQueuedTask, rotateExecutorKey, testExecutorWake, updateExecutor } from "../lib/api";
import type { TopicDraft } from "../domain/draft";
import { loadLocalState, loadNotionInfo, saveLocalState, saveNotionInfo } from "../lib/storage";
import { plainText } from "../lib/text";
import { postDuplicateKey, snapshotDuplicateKey } from "../domain/importing";
import {
  reduceDeletePost,
  reduceDeleteSnapshot,
  reducePatchFields,
  reduceSetStatus,
  reduceQueueOrder,
  reduceUpsertPost,
  reduceUpsertSnapshot,
} from "../domain/reduce";
import { applySyncResponse } from "../domain/sync";
import { isAccountId, isAddablePlatform, listedAccounts, newAccountId, topicAccount, DEFAULT_ACCOUNT_ID } from "../domain/accounts";
import {
  appendStyleVersion,
  clampPass,
  guideIsEmpty,
  latestScore,
  manualBenchmark,
  markStyleCurrent,
  passFor,
  rewriteComment,
  type BreakdownFields,
  type GuideFields,
  type NoteDraft,
} from "../domain/research";
import { normalizeExecutor, typeCovered, validateExecutorInput, wakeResultLine } from "../domain/executors";
import { buildReviewedRevision, readRevisions, rememberRevision, rewriteDirection, rewritingLabel, type DraftRevision, type RevisionImage } from "../domain/revisions";
import { buildTask, taskStatusLine } from "../domain/tasks";
import type { AccountId, AccountProfile, AccountSnapshot, AgentTask, BenchmarkNote, ExecutorProfile, FieldOutbox, LocalState, NotionSyncInfo, PlatformId, PlatformWeight, PostMetric, TaskType, Topic, TopicStatus } from "../types";
import { hrefFor, pathFor, resolveLocation, type RouteId } from "../routes";

export interface ToastUndo {
  topicId: string;
  status: TopicStatus;
}

export interface ToastAction {
  label: string;
  undo: ToastUndo | ToastUndo[];
}

export interface ToastItem {
  id: string;
  message: string;
  tone: "info" | "danger";
  action?: ToastAction;
}

interface StoreValue {
  userId: string;
  email: string;
  devPreview: boolean;
  state: LocalState;
  notion: NotionSyncInfo;
  online: boolean;
  syncing: boolean;
  authError: string | null;
  route: RouteId;
  navigate: (route: RouteId, search?: string) => void;
  href: string;
  query: string;
  setQuery: (value: string) => void;
  selectedTopicId: string | null;
  openTopic: (id: string | null) => void;
  reviewTopicId: string | null;
  openReview: (id: string | null) => void;
  loadDraft: (topicId: string) => Promise<TopicDraft>;
  returnDraft: (topicId: string, comment: string) => Promise<Topic>;
  returnForRewrite: (topicId: string, comment: string) => Promise<void>;
  approveEdited: (topicId: string, edit: { title: string; body: string; tags: string[] } | null) => Promise<void>;
  showRejected: boolean;
  setShowRejected: (value: boolean) => void;
  toasts: ToastItem[];
  dismissToast: (id: string) => void;
  notify: (message: string, tone?: ToastItem["tone"], action?: ToastAction) => void;
  setStatus: (topicId: string, status: TopicStatus) => void;
  saveFields: (topicId: string, fields: Omit<FieldOutbox, "id" | "kind" | "lastError" | "updatedAt" | "baseLastEditedTime" | "topicId">) => void;
  reorderQueue: (orderedIds: string[]) => void;
  saveSnapshot: (snapshot: AccountSnapshot, overwrite: boolean) => "saved" | "duplicate";
  deleteSnapshot: (id: string) => void;
  savePost: (post: PostMetric, overwrite: boolean) => "saved" | "duplicate";
  deletePost: (id: string) => void;
  importRecords: (snapshots: AccountSnapshot[], posts: PostMetric[], overwrite: boolean) => { saved: number; skipped: number };
  accountId: AccountId;
  setAccount: (accountId: AccountId) => void;
  agentReady: boolean;
  addAccountOpen: boolean;
  openAddAccount: (open: boolean) => void;
  addAccount: (input: { platform: string; displayName: string; profileUrl: string; note: string }) => AccountProfile | null;
  createTask: (input: { type: TaskType; platforms?: string[]; weights?: PlatformWeight[]; direction?: string; count?: number; topicId?: string | null; subjectId?: string | null; accountId?: AccountId }) => AgentTask;
  freshKey: { executorId: string; apiKey: string } | null;
  dismissFreshKey: () => void;
  saveExecutor: (input: { id?: string | null; name: string; wakeUrl: string; headerName: string; headerValue: string; taskTypes: string[]; enabled: boolean }) => Promise<boolean>;
  setExecutorEnabled: (id: string, enabled: boolean) => Promise<boolean>;
  regenerateExecutorKey: (id: string) => Promise<boolean>;
  testExecutorWake: (id: string) => Promise<void>;
  rewakeTask: (id: string) => Promise<void>;
  saveBenchmark: (input: NoteDraft) => BenchmarkNote | null;
  saveBreakdown: (id: string, fields: BreakdownFields) => void;
  saveStyleVersion: (fields: GuideFields) => string | null;
  useStyleVersion: (id: string) => void;
  setPassTotal: (value: number) => void;
  rewriteFromScore: (topicId: string) => Promise<void>;
  loadRevisions: (topicId: string) => Promise<DraftRevision[]>;
  regenerateFromAdvice: (input: {
    topicId: string;
    title: string;
    body: string;
    tags: string[];
    images: RevisionImage[];
    evaluation: string;
    suggestion: string;
    targets: string[];
    styleVersionId: string | null;
  }) => Promise<{ waiting: boolean; stored: boolean; remote: boolean; revision: DraftRevision }>;
  syncNow: (forceNotion?: boolean) => Promise<void>;
  signOut?: () => void;
}

const StoreContext = createContext<StoreValue | null>(null);

export function StoreProvider({
  userId,
  email,
  devPreview,
  getToken,
  signOut,
  initialState,
  initialNotion,
  children,
}: {
  userId: string;
  email: string;
  devPreview: boolean;
  getToken: (() => Promise<string | null>) | null;
  signOut?: () => void;
  initialState?: LocalState;
  initialNotion?: NotionSyncInfo;
  children: ReactNode;
}) {
  const [state, setState] = useState<LocalState>(() => initialState ?? loadLocalState(userId));
  const [notion, setNotion] = useState<NotionSyncInfo>(() => initialNotion ?? loadNotionInfo(userId));
  const [online, setOnline] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [route, setRoute] = useState<RouteId>(() => resolveLocation(window.location.pathname, window.location.search).route);
  const [href, setHref] = useState(() => hrefFor(resolveLocation(window.location.pathname, window.location.search)));
  const [query, setQuery] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [reviewTopicId, setReviewTopicId] = useState<string | null>(null);
  const [showRejected, setShowRejected] = useState(false);
  const [accountId, setAccountId] = useState<AccountId>(() => readAccount(userId));
  const [agentReady, setAgentReady] = useState(() => (getToken ? readAgentReady(userId) : false));
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [freshKey, setFreshKey] = useState<{ executorId: string; apiKey: string } | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const stateRef = useRef(state);
  const syncingRef = useRef(false);
  useEffect(() => {
    const listed = listedAccounts(state.accounts ?? [], {
      topics: state.topics,
      tasks: state.tasks ?? [],
      snapshots: state.snapshots,
      postMetrics: state.postMetrics,
    });
    if (listed.some((item) => item.id === accountId)) return;
    setAccountId(DEFAULT_ACCOUNT_ID);
    try {
      localStorage.setItem(accountKey(userId), DEFAULT_ACCOUNT_ID);
    } catch {
      /* private mode */
    }
  }, [accountId, state.accounts, state.postMetrics, state.snapshots, state.tasks, state.topics, userId]);
  const queuedRef = useRef(false);
  stateRef.current = state;

  const notify = useCallback((message: string, tone: ToastItem["tone"] = "info", action?: ToastAction) => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current.slice(-3), { id, message, tone, action }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, action ? 6000 : 3600);
  }, []);

  const commit = useCallback(
    (updater: (current: LocalState) => LocalState) => {
      const next = updater(stateRef.current);
      stateRef.current = next;
      setState(next);
      saveLocalState(userId, next);
      return next;
    },
    [userId],
  );

  const syncNow = useCallback(
    async (forceNotion = false) => {
      if (!getToken) {
        setOnline(false);
        return;
      }
      if (syncingRef.current) {
        queuedRef.current = true;
        return;
      }
      syncingRef.current = true;
      setSyncing(true);
      const sent = stateRef.current;
      const pendingActivityIds = new Set(
        sent.outbox.flatMap((item) => (item.kind === "set_status" ? [item.activityId] : [])),
      );
      try {
        const token = await getToken();
        if (!token) throw new ApiError(401, "请先登录。");
        const response = await postSync(
          {
            forceNotion,
            outbox: sent.outbox,
            activities: sent.activities.filter((activity) => !pendingActivityIds.has(activity.id)),
            snapshots: sent.snapshots,
            postMetrics: sent.postMetrics,
            tasks: sent.tasks ?? [],
            accounts: sent.accounts ?? [],
            benchmarks: sent.benchmarks ?? [],
            styleGuides: sent.styleGuides ?? [],
            postScores: sent.postScores ?? [],
            scoreSettings: sent.scoreSettings ?? [],
          },
          token,
        );
        const next = applySyncResponse(
          stateRef.current,
          sent.outbox.map((item) => item.id),
          sent.snapshots,
          sent.postMetrics,
          sent.activities.filter((activity) => !pendingActivityIds.has(activity.id)),
          response,
          sent.tasks ?? [],
          sent.accounts ?? [],
          sent.benchmarks ?? [],
          sent.styleGuides ?? [],
          sent.postScores ?? [],
          sent.scoreSettings ?? [],
        );
        stateRef.current = next;
        setState(next);
        saveLocalState(userId, next);
        setNotion(response.notion);
        saveNotionInfo(userId, response.notion);
        setOnline(true);
        setAuthError(null);
        const ready = Boolean(response.agentConfigured);
        setAgentReady(ready);
        try {
          localStorage.setItem(agentKey(userId), ready ? "1" : "0");
        } catch {
          /* private mode */
        }
        if (forceNotion && response.notion.lastError) notify(response.notion.lastError, "danger");
        else if (forceNotion) notify("已从 Notion 同步。");
        const failed = response.results.find((item) => !item.ok && !item.conflict);
        if (failed?.error) notify(failed.error, "danger");
        else if (response.results.some((item) => item.conflict)) notify("有一条选题以 Notion 的更新为准。", "danger");
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403 || error.status === 503)) {
          setAuthError(error.message);
          setOnline(false);
        } else {
          setOnline(false);
          if (forceNotion) notify(error instanceof Error ? error.message : "同步失败，改动先留在这台设备上。", "danger");
        }
      } finally {
        syncingRef.current = false;
        setSyncing(false);
        if (queuedRef.current) {
          queuedRef.current = false;
          void syncNow(false);
        }
      }
    },
    [getToken, notify, userId],
  );

  useEffect(() => {
    void syncNow(false);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void syncNow(false);
    }, 30_000);
    const onOnline = () => void syncNow(false);
    window.addEventListener("online", onOnline);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("online", onOnline);
    };
  }, [syncNow]);

  useEffect(() => {
    const apply = () => {
      const target = resolveLocation(window.location.pathname, window.location.search);
      const next = hrefFor(target);
      if (next !== window.location.pathname + window.location.search) window.history.replaceState(null, "", next);
      setRoute(target.route);
      setHref(next);
    };
    apply();
    window.addEventListener("popstate", apply);
    return () => window.removeEventListener("popstate", apply);
  }, []);

  const navigate = useCallback((next: RouteId, search = "") => {
    const path = `${pathFor(next)}${search ? `?${search}` : ""}`;
    if (path !== window.location.pathname + window.location.search) window.history.pushState(null, "", path);
    setRoute(next);
    setHref(path);
  }, []);

  const rememberExecutor = useCallback((executor: ExecutorProfile, apiKey?: string) => {
    commit((current) => ({
      ...current,
      executors: [...(current.executors ?? []).filter((item) => item.id !== executor.id), executor]
        .sort((left, right) => left.createdAt.localeCompare(right.createdAt)),
    }));
    if (apiKey) setFreshKey({ executorId: executor.id, apiKey });
  }, [commit]);

  const executorToken = useCallback(async (): Promise<string | null> => {
    if (!getToken) {
      notify(copy.executors.local, "danger");
      return null;
    }
    const token = await getToken();
    if (!token) {
      notify("请先登录。", "danger");
      return null;
    }
    return token;
  }, [getToken, notify]);

  const saveExecutor = useCallback(async (input: {
    id?: string | null;
    name: string;
    wakeUrl: string;
    headerName: string;
    headerValue: string;
    taskTypes: string[];
    enabled: boolean;
  }): Promise<boolean> => {
    const parsed = validateExecutorInput(input, Boolean(input.id));
    if ("error" in parsed) {
      notify(copy.executors.errors[parsed.error], "danger");
      return false;
    }
    try {
      const token = await executorToken();
      if (!token) return false;
      const body = { ...parsed.value, enabled: input.id ? input.enabled : true };
      const created = input.id ? null : await createExecutor(body, token);
      const updated = input.id ? await updateExecutor(input.id, body, token) : null;
      const executor = normalizeExecutor(created?.executor ?? updated?.executor);
      if (!executor) throw new ApiError(500, "执行者没有保存完整。");
      rememberExecutor(executor, created?.apiKey);
      notify(input.id ? "已保存。" : "已添加执行者。密钥只显示这一次。");
      return true;
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "没有保存成功。", "danger");
      return false;
    }
  }, [executorToken, notify, rememberExecutor]);

  const setExecutorEnabled = useCallback(async (id: string, enabled: boolean): Promise<boolean> => {
    const current = (stateRef.current.executors ?? []).find((item) => item.id === id);
    if (!current) return false;
    return saveExecutor({
      id,
      name: current.name,
      wakeUrl: current.wakeUrl,
      headerName: current.headerName,
      headerValue: "",
      taskTypes: current.taskTypes,
      enabled,
    });
  }, [saveExecutor]);

  const regenerateExecutorKey = useCallback(async (id: string): Promise<boolean> => {
    try {
      const token = await executorToken();
      if (!token) return false;
      const response = await rotateExecutorKey(id, token);
      const executor = normalizeExecutor(response.executor);
      if (!executor) throw new ApiError(500, "密钥没有换成功。");
      rememberExecutor(executor, response.apiKey);
      notify(copy.executors.regenWarn);
      return true;
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "密钥没有换成功。", "danger");
      return false;
    }
  }, [executorToken, notify, rememberExecutor]);

  const testExecutorWakeAction = useCallback(async (id: string): Promise<void> => {
    try {
      const token = await executorToken();
      if (!token) return;
      const response = await testExecutorWake(id, token);
      const executor = normalizeExecutor(response.executor);
      if (executor) rememberExecutor(executor);
      notify(wakeResultLine([response.result]));
      void syncNow(false);
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "测试唤醒没有发出去。", "danger");
    }
  }, [executorToken, notify, rememberExecutor, syncNow]);

  const rewakeTask = useCallback(async (id: string): Promise<void> => {
    try {
      const token = await executorToken();
      if (!token) return;
      const response = await rewakeQueuedTask(id, token);
      notify(response.results.length ? wakeResultLine(response.results) : (response.error || wakeResultLine([])));
      void syncNow(false);
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "没有重新唤醒。", "danger");
    }
  }, [executorToken, notify, syncNow]);

  const value = useMemo<StoreValue>(
    () => ({
      userId,
      email,
      devPreview,
      state,
      notion,
      online,
      syncing,
      authError,
      route,
      navigate,
      href,
      query,
      setQuery,
      selectedTopicId,
      openTopic: (id) => {
        setSelectedTopicId(id);
        if (id) setReviewTopicId(null);
      },
      reviewTopicId,
      openReview: (id) => {
        setReviewTopicId(id);
        if (id) {
          setSelectedTopicId(null);
          navigate("review", `topic=${encodeURIComponent(id)}`);
        }
      },
      loadDraft: async (topicId) => {
        if (!getToken) {
          if (import.meta.env.DEV) {
            const raw = sessionStorage.getItem("mediadesk.draft");
            if (raw) return JSON.parse(raw) as TopicDraft;
          }
          throw new ApiError(0, "本地预览读不到 Notion 正文。登录后的预览可以打开稿子。");
        }
        const token = await getToken();
        if (!token) throw new ApiError(401, "请先登录。");
        return fetchDraft(topicId, token);
      },
      returnForRewrite: async (topicId, comment) => {
        const text = comment.trim();
        if (!text) throw new ApiError(400, copy.flow.reasonNeeded);
        const topic = stateRef.current.topics.find((item) => item.id === topicId);
        if (!topic) return;
        if (getToken) {
          const token = await getToken();
          if (!token) throw new ApiError(401, "请先登录。");
          const updated = await postReturn(topicId, text, token);
          commit((state) => ({
            ...state,
            topics: state.topics.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
            outbox: state.outbox.filter((item) => !(item.kind === "set_status" && item.topicId === updated.id)),
          }));
          notify(copy.status.returned(topic.title));
          void syncNow(false);
          return;
        }
        const now = new Date().toISOString();
        commit((state) => {
          const withStatus = reduceSetStatus(state, topicId, "制作中", now, {
            outboxId: crypto.randomUUID(),
            activityId: crypto.randomUUID(),
          });
          const fresh = withStatus.topics.find((item) => item.id === topicId);
          const notes = [fresh?.notes?.trim(), text].filter(Boolean).join("\n\n");
          return reducePatchFields(withStatus, { topicId, notes }, now, crypto.randomUUID());
        });
        notify(`${copy.status.returned(topic.title)} 本地预览写不回 Notion。`);
      },
      approveEdited: async (topicId, edit) => {
        const topic = stateRef.current.topics.find((item) => item.id === topicId);
        if (!topic) return;
        if (edit && getToken) {
          const token = await getToken();
          if (!token) throw new ApiError(401, "请先登录。");
          const updated = await postApprove(topicId, edit, token);
          commit((state) => ({
            ...state,
            topics: state.topics.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
            outbox: state.outbox.filter((item) => !(item.kind === "set_status" && item.topicId === updated.id)),
          }));
          notify(copy.status.approved(plainText(updated.title)));
          void syncNow(false);
          return;
        }
        const now = new Date().toISOString();
        commit((state) => {
          let next = reduceSetStatus(state, topicId, "待发布", now, {
            outboxId: crypto.randomUUID(),
            activityId: crypto.randomUUID(),
          });
          if (!edit) return next;
          const title = edit.title.trim();
          if (title) {
            next = {
              ...next,
              topics: next.topics.map((item) => (item.id === topicId ? { ...item, title } : item)),
            };
          }
          const note = ["发布稿", title ? `标题：${title}` : "", edit.body.trim() ? `正文：${edit.body.trim()}` : "", edit.tags.length ? `标签：${edit.tags.join(" ")}` : ""]
            .filter(Boolean)
            .join("\n");
          return reducePatchFields(next, { topicId, notes: note }, now, crypto.randomUUID());
        });
        notify(edit ? copy.flow.localWrite : copy.status.approved(plainText(topic.title)));
        void syncNow(false);
      },
      returnDraft: async (topicId, comment) => {
        if (!getToken) throw new ApiError(0, "本地预览写不回 Notion。");
        const token = await getToken();
        if (!token) throw new ApiError(401, "请先登录。");
        const topic = await postReturn(topicId, comment, token);
        commit((current) => ({
          ...current,
          topics: current.topics.map((item) => (item.id === topic.id ? { ...item, ...topic } : item)),
          outbox: current.outbox.filter((item) => !(item.kind === "set_status" && item.topicId === topic.id)),
        }));
        void syncNow(false);
        return topic;
      },
      showRejected,
      setShowRejected,
      toasts,
      dismissToast: (id) => setToasts((current) => current.filter((item) => item.id !== id)),
      notify,
      setStatus: (topicId, status) => {
        commit((current) =>
          reduceSetStatus(current, topicId, status, new Date().toISOString(), {
            outboxId: crypto.randomUUID(),
            activityId: crypto.randomUUID(),
          }),
        );
        void syncNow(false);
      },
      saveFields: (topicId, fields) => {
        commit((current) => reducePatchFields(current, { topicId, ...fields }, new Date().toISOString(), crypto.randomUUID()));
        notify("已保存，正在写回。");
        void syncNow(false);
      },
      reorderQueue: (orderedIds) => {
        commit((current) => reduceQueueOrder(current, orderedIds, new Date().toISOString(), () => crypto.randomUUID()));
        notify(copy.progress.saved);
        void syncNow(false);
      },
      saveSnapshot: (snapshot, overwrite) => {
        const duplicate = stateRef.current.snapshots.some(
          (item) => item.deletedAt == null && item.id !== snapshot.id && snapshotDuplicateKey(item) === snapshotDuplicateKey(snapshot),
        );
        if (duplicate && !overwrite) return "duplicate";
        commit((current) => reduceUpsertSnapshot(current, snapshot, overwrite));
        void syncNow(false);
        return "saved";
      },
      deleteSnapshot: (id) => {
        commit((current) => reduceDeleteSnapshot(current, id, new Date().toISOString()));
        void syncNow(false);
      },
      savePost: (post, overwrite) => {
        const duplicate = stateRef.current.postMetrics.some(
          (item) => item.deletedAt == null && item.id !== post.id && postDuplicateKey(item) === postDuplicateKey(post),
        );
        if (duplicate && !overwrite) return "duplicate";
        commit((current) => reduceUpsertPost(current, post, overwrite));
        void syncNow(false);
        return "saved";
      },
      deletePost: (id) => {
        commit((current) => reduceDeletePost(current, id, new Date().toISOString()));
        void syncNow(false);
      },
      accountId,
      setAccount: (next) => {
        setAccountId(next);
        try {
          localStorage.setItem(accountKey(userId), next);
        } catch {
          /* private mode */
        }
      },
      agentReady,
      addAccountOpen,
      openAddAccount: setAddAccountOpen,
      addAccount: (input) => {
        const name = input.displayName.trim().slice(0, 80);
        if (!name || !isAddablePlatform(input.platform)) return null;
        const now = new Date().toISOString();
        const id = newAccountId(input.platform, stateRef.current.accounts ?? []);
        const profile: AccountProfile = {
          id,
          platform: input.platform,
          displayName: name,
          profileUrl: input.profileUrl.trim().slice(0, 500),
          note: input.note.trim().slice(0, 500),
          addedAt: now,
          updatedAt: now,
        };
        commit((current) => ({
          ...current,
          accounts: [...(current.accounts ?? []).filter((item) => item.id !== id), profile],
        }));
        setAccountId(id);
        try {
          localStorage.setItem(accountKey(userId), id);
        } catch {
          /* private mode */
        }
        notify(`已添加「${name}」。`);
        void syncNow(false);
        return profile;
      },
      createTask: (input) => {
        const now = new Date().toISOString();
        const task = buildTask(
          {
            type: input.type,
            accountId: input.accountId && isAccountId(input.accountId) ? input.accountId : accountId,
            platforms: input.platforms,
            weights: input.weights,
            direction: input.direction,
            count: input.count,
            topicId: input.topicId,
            subjectId: input.subjectId,
          },
          now,
          crypto.randomUUID(),
        );
        commit((current) => ({ ...current, tasks: [task, ...(current.tasks ?? [])] }));
        notify(taskStatusLine(task, agentReady, typeCovered(stateRef.current.executors ?? [], task.type)));
        void syncNow(false);
        return task;
      },
      freshKey,
      dismissFreshKey: () => setFreshKey(null),
      saveExecutor,
      setExecutorEnabled,
      regenerateExecutorKey,
      testExecutorWake: testExecutorWakeAction,
      rewakeTask,
      saveBenchmark: (input) => {
        const now = new Date().toISOString();
        const result = manualBenchmark(input, accountId, now, crypto.randomUUID());
        if ("error" in result) return null;
        commit((current) => ({ ...current, benchmarks: [result.note, ...(current.benchmarks ?? [])] }));
        notify("已记下这条对标。");
        void syncNow(false);
        return result.note;
      },
      saveBreakdown: (id, fields) => {
        const now = new Date().toISOString();
        commit((current) => ({
          ...current,
          benchmarks: (current.benchmarks ?? []).map((item) =>
            item.id === id
              ? {
                  ...item,
                  titlePattern: fields.titlePattern.trim().slice(0, 2000),
                  coverType: fields.coverType.trim().slice(0, 2000),
                  bodyStructure: fields.bodyStructure.trim().slice(0, 2000),
                  aiTools: fields.aiTools.trim().slice(0, 2000),
                  interaction: fields.interaction.trim().slice(0, 2000),
                  whyViral: fields.whyViral.trim().slice(0, 2000),
                  updatedAt: now,
                }
              : item,
          ),
        }));
        notify("已保存拆解。");
        void syncNow(false);
      },
      saveStyleVersion: (fields) => {
        if (guideIsEmpty(fields)) return null;
        const now = new Date().toISOString();
        const id = crypto.randomUUID();
        commit((current) => ({ ...current, styleGuides: appendStyleVersion(current.styleGuides ?? [], accountId, fields, now, id) }));
        notify("已保存为新版本。");
        void syncNow(false);
        return id;
      },
      useStyleVersion: (id) => {
        const now = new Date().toISOString();
        commit((current) => ({ ...current, styleGuides: markStyleCurrent(current.styleGuides ?? [], id, now) }));
        void syncNow(false);
      },
      setPassTotal: (value) => {
        const now = new Date().toISOString();
        const row = { id: accountId, accountId, passTotal: clampPass(value), updatedAt: now };
        commit((current) => ({
          ...current,
          scoreSettings: [row, ...(current.scoreSettings ?? []).filter((item) => item.accountId !== accountId)],
        }));
        void syncNow(false);
      },
      rewriteFromScore: async (topicId) => {
        const current = stateRef.current;
        const score = latestScore(current.postScores ?? [], topicId);
        const topic = current.topics.find((item) => item.id === topicId);
        if (!score || !topic) return;
        const comment = rewriteComment(score, passFor(current.scoreSettings ?? [], topicAccount(topic)));
        if (getToken) {
          const token = await getToken();
          if (!token) throw new ApiError(401, "请先登录。");
          const updated = await postReturn(topicId, comment, token);
          commit((state) => ({
            ...state,
            topics: state.topics.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
            outbox: state.outbox.filter((item) => !(item.kind === "set_status" && item.topicId === updated.id)),
          }));
          notify(copy.status.returned(topic.title));
          void syncNow(false);
          return;
        }
        const now = new Date().toISOString();
        commit((state) => {
          const withStatus = reduceSetStatus(state, topicId, "制作中", now, {
            outboxId: crypto.randomUUID(),
            activityId: crypto.randomUUID(),
          });
          const fresh = withStatus.topics.find((item) => item.id === topicId);
          const notes = [fresh?.notes?.trim(), comment].filter(Boolean).join("\n\n");
          return reducePatchFields(withStatus, { topicId, notes }, now, crypto.randomUUID());
        });
        notify(copy.status.returned(topic.title));
        void syncNow(false);
      },
      loadRevisions: async (topicId) => {
        const local = readRevisions(topicId);
        if (!getToken) return local;
        try {
          const token = await getToken();
          if (!token) return local;
          const remote = await fetchRevisions(topicId, token);
          for (const revision of remote.revisions) rememberRevision(revision);
          const seen = new Set(remote.revisions.map((item) => item.id));
          return [...remote.revisions, ...local.filter((item) => !seen.has(item.id))].sort(
            (left, right) => left.version - right.version || left.createdAt.localeCompare(right.createdAt),
          );
        } catch {
          return local;
        }
      },
      regenerateFromAdvice: async (input) => {
        const now = new Date().toISOString();
        const existing = readRevisions(input.topicId);
        const taskId = crypto.randomUUID();
        const built = buildReviewedRevision({ ...input, existing }, now, crypto.randomUUID(), taskId);
        if ("error" in built) throw new ApiError(400, built.error);
        const topic = stateRef.current.topics.find((item) => item.id === input.topicId);
        if (!topic) throw new ApiError(404, "找不到这篇稿子。");
        if (getToken) {
          const token = await getToken();
          if (!token) throw new ApiError(401, "请先登录。");
          const result = await postRewrite(input.topicId, input, token);
          rememberRevision(result.revision);
          commit((state) => ({
            ...state,
            topics: state.topics.map((item) => (item.id === result.topic.id ? { ...item, ...result.topic } : item)),
            tasks: [result.task, ...(state.tasks ?? []).filter((item) => item.id !== result.task.id)],
            outbox: state.outbox.filter((item) => !(item.kind === "set_status" && item.topicId === result.topic.id)),
          }));
          notify(result.waiting ? copy.flow.waiting : rewritingLabel(result.revision.version + 1));
          void syncNow(false);
          return { waiting: result.waiting, stored: result.stored, remote: true, revision: result.revision };
        }
        rememberRevision(built.revision);
        const task = buildTask(
          {
            type: "按建议重写",
            accountId: topicAccount(topic),
            platforms: topic.platforms,
            topicId: topic.id,
            count: 1,
            direction: rewriteDirection(built.revision.version + 1, built.revision.suggestion),
          },
          now,
          taskId,
        );
        const waiting = !typeCovered(stateRef.current.executors ?? [], "按建议重写");
        commit((state) => {
          const withStatus = reduceSetStatus(state, topic.id, "制作中", now, {
            outboxId: crypto.randomUUID(),
            activityId: crypto.randomUUID(),
          });
          const fresh = withStatus.topics.find((item) => item.id === topic.id);
          const notes = [fresh?.notes?.trim(), built.revision.suggestion].filter(Boolean).join("\n\n");
          const patched = reducePatchFields(withStatus, { topicId: topic.id, notes }, now, crypto.randomUUID());
          return { ...patched, tasks: [task, ...(patched.tasks ?? [])] };
        });
        notify(waiting ? copy.flow.waiting : `${rewritingLabel(built.revision.version + 1)}。${copy.flow.localRewrite}`);
        return { waiting, stored: true, remote: false, revision: built.revision };
      },
      importRecords: (snapshots, posts, overwrite) => {
        let saved = 0;
        let skipped = 0;
        commit((current) => {
          let next = current;
          for (const snapshot of snapshots) {
            const duplicate = next.snapshots.some(
              (item) =>
                item.deletedAt == null &&
                item.id !== snapshot.id &&
                snapshotDuplicateKey(item) === snapshotDuplicateKey(snapshot),
            );
            if (duplicate && !overwrite) {
              skipped += 1;
              continue;
            }
            const before = next;
            next = reduceUpsertSnapshot(next, snapshot, overwrite);
            if (next !== before) saved += 1;
          }
          for (const post of posts) {
            const duplicate = next.postMetrics.some(
              (item) => item.deletedAt == null && item.id !== post.id && postDuplicateKey(item) === postDuplicateKey(post),
            );
            if (duplicate && !overwrite) {
              skipped += 1;
              continue;
            }
            const before = next;
            next = reduceUpsertPost(next, post, overwrite);
            if (next !== before) saved += 1;
          }
          return next;
        });
        void syncNow(false);
        return { saved, skipped };
      },
      syncNow,
      signOut,
    }),
    [
      accountId,
      addAccountOpen,
      agentReady,
      authError,
      commit,
      devPreview,
      freshKey,
      rewakeTask,
      regenerateExecutorKey,
      saveExecutor,
      setExecutorEnabled,
      testExecutorWakeAction,
      email,
      getToken,
      href,
      navigate,
      notion,
      notify,
      online,
      query,
      route,
      reviewTopicId,
      selectedTopicId,
      showRejected,
      signOut,
      state,
      syncNow,
      syncing,
      toasts,
      userId,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useApp(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) throw new Error("Store 尚未就绪");
  return value;
}

function accountKey(userId: string): string {
  return `mediadesk.account.${userId}`;
}

function readAccount(userId: string): AccountId {
  try {
    const value = localStorage.getItem(accountKey(userId));
    return isAccountId(value) ? value : DEFAULT_ACCOUNT_ID;
  } catch {
    return DEFAULT_ACCOUNT_ID;
  }
}

function agentKey(userId: string): string {
  return `mediadesk.agent.${userId}`;
}

function readAgentReady(userId: string): boolean {
  try {
    return localStorage.getItem(agentKey(userId)) === "1";
  } catch {
    return false;
  }
}

export function newId(): string {
  return crypto.randomUUID();
}

export type { PlatformId };
